import React, { useCallback, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { Avatar, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useNavigate } from 'react-router-dom';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { DateTime } from 'luxon';
import { IntlShape, useIntl } from 'react-intl';

import { Button } from '../../components/design-system';
import { formatDate } from '../../helpers';
import { useMaterialReactTableLocalization } from '../../i18n';
import { GET_BLOCKED_USERS_QUERY, UNBLOCK_USER_MUTATION } from '../../gql';
import {
  ContactRow,
  BlockedUserRow,
  getContactInitials,
  getContactName,
  IncomingRequestRow,
  SentRequestRow,
} from './types';

interface IncomingRequestsTableProps {
  loading?: boolean;
  onAccept: (id: string) => void;
  onRemove: (id: string) => void;
  rows: IncomingRequestRow[];
}

interface SentRequestsTableProps {
  loading?: boolean;
  onRemove: (id: string) => void;
  rows: SentRequestRow[];
}

interface BlockedUsersTableProps {
  loading?: boolean;
  rows: BlockedUserRow[];
}

const tablePaperStyles = {
  backgroundImage: 'none',
  border: '1px solid',
  borderColor: 'softBorder',
  boxShadow: 'none',
};
const tableHeadCellStyles = { fontWeight: 650 };
const tableCellStyles = { borderColor: 'softBorder' };
const identityStyles = { alignItems: 'center', minWidth: 0 };
const avatarStyles = {
  width: 34,
  height: 34,
  fontSize: '0.8125rem',
  bgcolor: 'action.selected',
  color: 'text.primary',
};
const emailStyles = { color: 'text.secondary' };
const actionStyles = { justifyContent: 'flex-start' };

const createBaseTableOptions = (loading: boolean) => ({
  enableColumnActions: false,
  enableColumnFilters: false,
  enableDensityToggle: false,
  enableFullScreenToggle: false,
  enableGlobalFilter: false,
  enableHiding: false,
  enableTopToolbar: false,
  initialState: { pagination: { pageIndex: 0, pageSize: 25 } },
  muiPaginationProps: { showRowsPerPage: false },
  muiTableBodyCellProps: { sx: tableCellStyles },
  muiTableHeadCellProps: { sx: tableHeadCellStyles },
  muiTablePaperProps: { sx: tablePaperStyles },
  state: { isLoading: loading },
});

const ContactIdentity = React.memo(
  ({ contact }: { contact: Pick<ContactRow, 'displayName' | 'email'> }) => (
    <Stack direction="row" gap={1.25} sx={identityStyles}>
      <Avatar sx={avatarStyles}>{getContactInitials(contact)}</Avatar>
      <Typography noWrap variant="body2">
        {getContactName(contact)}
      </Typography>
    </Stack>
  )
);

ContactIdentity.displayName = 'ContactIdentity';

const SharingSummary = React.memo(
  ({ groupCount, siteCount }: { groupCount: number; siteCount: number }) => {
    const intl = useIntl();
    const sites = intl.formatMessage(
      {
        id: 'contacts.sharing.siteCount',
        defaultMessage: '{count, plural, one {# site} other {# sites}}',
      },
      { count: siteCount }
    );
    const groups = intl.formatMessage(
      {
        id: 'contacts.sharing.groupCount',
        defaultMessage: '{count, plural, one {# group} other {# groups}}',
      },
      { count: groupCount }
    );

    return (
      <Typography noWrap variant="body2" color="text.secondary">
        {sites} · {groups}
      </Typography>
    );
  }
);

SharingSummary.displayName = 'SharingSummary';

const ContactDetailAction = React.memo(({ id }: { id: string }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const handleClick = useCallback(() => {
    navigate(`/contacts/${id}`);
  }, [id, navigate]);

  return (
    <Stack direction="row" gap={0.5} sx={actionStyles}>
      <Button density="compact" onClick={handleClick} tone="neutral">
        {intl.formatMessage({
          id: 'general.details',
          defaultMessage: 'Details',
        })}
      </Button>
      <ChevronRightIcon color="action" fontSize="small" />
    </Stack>
  );
});

ContactDetailAction.displayName = 'ContactDetailAction';

const createContactColumns = (intl: IntlShape): MRT_ColumnDef<ContactRow>[] => [
  {
    accessorFn: getContactName,
    header: intl.formatMessage({
      id: 'contacts.table.contact',
      defaultMessage: 'Contact',
    }),
    Cell: ({ row }) => <ContactIdentity contact={row.original} />,
    size: 190,
  },
  {
    accessorKey: 'email',
    header: intl.formatMessage({
      id: 'account.email',
      defaultMessage: 'Email',
    }),
    Cell: ({ cell }) => (
      <Typography noWrap variant="body2" sx={emailStyles}>
        {cell.getValue<string>()}
      </Typography>
    ),
    size: 220,
  },
  {
    accessorFn: (row) => new Date(row.createdAt),
    header: intl.formatMessage({
      id: 'contacts.added',
      defaultMessage: 'Added',
    }),
    Cell: ({ cell }) => formatDate(cell.getValue<Date>()),
    size: 125,
  },
  {
    id: 'sharedWithMe',
    header: intl.formatMessage({
      id: 'contacts.table.sharesWithYou',
      defaultMessage: 'Shares with you',
    }),
    Cell: ({ row }) => (
      <SharingSummary
        groupCount={row.original.sharedWithMeGroupCount}
        siteCount={row.original.sharedWithMeSiteCount}
      />
    ),
    enableSorting: false,
    size: 190,
  },
  {
    id: 'sharedByMe',
    header: intl.formatMessage({
      id: 'contacts.table.youShare',
      defaultMessage: 'You share',
    }),
    Cell: ({ row }) => (
      <SharingSummary
        groupCount={row.original.sharedByMeGroupCount}
        siteCount={row.original.sharedByMeSiteCount}
      />
    ),
    enableSorting: false,
    size: 190,
  },
  {
    id: 'actions',
    header: intl.formatMessage({
      id: 'general.actions',
      defaultMessage: 'Actions',
    }),
    Cell: ({ row }) => <ContactDetailAction id={row.original.id} />,
    enableSorting: false,
    size: 125,
  },
];

const ContactsTable = ({
  loading = false,
  rows,
}: {
  loading?: boolean;
  rows: ContactRow[];
}) => {
  const intl = useIntl();
  const localization = useMaterialReactTableLocalization();
  const columns = useMemo(() => createContactColumns(intl), [intl]);
  const table = useMaterialReactTable({
    ...createBaseTableOptions(loading),
    columns,
    data: rows,
    localization,
  });

  return <MaterialReactTable table={table} />;
};

interface RequestActionsProps {
  id: string;
  onAccept: (id: string) => void;
  onRemove: (id: string) => void;
}

const RequestActions = React.memo(
  ({ id, onAccept, onRemove }: RequestActionsProps) => {
    const intl = useIntl();
    const handleAccept = useCallback(() => onAccept(id), [id, onAccept]);
    const handleRemove = useCallback(() => onRemove(id), [id, onRemove]);

    return (
      <Stack direction="row" gap={1} sx={actionStyles}>
        <Button
          density="compact"
          onClick={handleAccept}
          startIcon={<CheckIcon />}
          tone="success"
        >
          {intl.formatMessage({
            id: 'general.accept',
            defaultMessage: 'Accept',
          })}
        </Button>
        <Button
          density="compact"
          onClick={handleRemove}
          startIcon={<CloseIcon />}
          tone="danger"
          variant="outlined"
        >
          {intl.formatMessage({
            id: 'general.remove',
            defaultMessage: 'Remove',
          })}
        </Button>
      </Stack>
    );
  }
);

RequestActions.displayName = 'RequestActions';

const createIncomingColumns = (
  intl: IntlShape,
  onAccept: (id: string) => void,
  onRemove: (id: string) => void
): MRT_ColumnDef<IncomingRequestRow>[] => [
  {
    accessorFn: getContactName,
    header: intl.formatMessage({
      id: 'contacts.requests.applicant',
      defaultMessage: 'Applicant',
    }),
    Cell: ({ row }) => <ContactIdentity contact={row.original} />,
  },
  {
    accessorKey: 'email',
    header: intl.formatMessage({
      id: 'account.email',
      defaultMessage: 'Email',
    }),
  },
  {
    accessorFn: (row) => new Date(row.createdAt),
    header: intl.formatMessage({
      id: 'contacts.requests.received',
      defaultMessage: 'Received',
    }),
    Cell: ({ cell }) =>
      DateTime.fromJSDate(cell.getValue<Date>()).toRelative({
        locale: intl.locale,
      }),
  },
  {
    id: 'actions',
    header: intl.formatMessage({
      id: 'general.actions',
      defaultMessage: 'Actions',
    }),
    Cell: ({ row }) => (
      <RequestActions
        id={row.original.id}
        onAccept={onAccept}
        onRemove={onRemove}
      />
    ),
    enableSorting: false,
  },
];

const IncomingRequestsTable = ({
  loading = false,
  onAccept,
  onRemove,
  rows,
}: IncomingRequestsTableProps) => {
  const intl = useIntl();
  const localization = useMaterialReactTableLocalization();
  const columns = useMemo(
    () => createIncomingColumns(intl, onAccept, onRemove),
    [intl, onAccept, onRemove]
  );
  const table = useMaterialReactTable({
    ...createBaseTableOptions(loading),
    columns,
    data: rows,
    localization,
  });

  return <MaterialReactTable table={table} />;
};

const SentRequestAction = React.memo(
  ({ id, onRemove }: { id: string; onRemove: (id: string) => void }) => {
    const intl = useIntl();
    const handleRemove = useCallback(() => onRemove(id), [id, onRemove]);

    return (
      <Stack direction="row" sx={actionStyles}>
        <Button
          density="compact"
          onClick={handleRemove}
          tone="danger"
          variant="outlined"
        >
          {intl.formatMessage({
            id: 'contacts.requests.cancel',
            defaultMessage: 'Cancel request',
          })}
        </Button>
      </Stack>
    );
  }
);

SentRequestAction.displayName = 'SentRequestAction';

const createSentColumns = (
  intl: IntlShape,
  onRemove: (id: string) => void
): MRT_ColumnDef<SentRequestRow>[] => [
  {
    accessorKey: 'email',
    header: intl.formatMessage({
      id: 'contacts.requests.recipient',
      defaultMessage: 'Recipient',
    }),
  },
  {
    accessorFn: (row) => new Date(row.createdAt),
    header: intl.formatMessage({
      id: 'contacts.sent',
      defaultMessage: 'Sent',
    }),
    Cell: ({ cell }) => formatDate(cell.getValue<Date>()),
  },
  {
    id: 'actions',
    header: intl.formatMessage({
      id: 'general.actions',
      defaultMessage: 'Actions',
    }),
    Cell: ({ row }) => (
      <SentRequestAction id={row.original.id} onRemove={onRemove} />
    ),
    enableSorting: false,
  },
];

const SentRequestsTable = ({
  loading = false,
  onRemove,
  rows,
}: SentRequestsTableProps) => {
  const intl = useIntl();
  const localization = useMaterialReactTableLocalization();
  const columns = useMemo(() => createSentColumns(intl, onRemove), [
    intl,
    onRemove,
  ]);
  const table = useMaterialReactTable({
    ...createBaseTableOptions(loading),
    columns,
    data: rows,
    localization,
  });

  return <MaterialReactTable table={table} />;
};

const unblockIcon = <LockOpenIcon />;

function UnblockAction({ userId }: { userId: string }) {
  const intl = useIntl();
  const [unblockUser, { loading, error }] = useMutation(UNBLOCK_USER_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_BLOCKED_USERS_QUERY }],
  });

  const handleUnblock = async () => {
    try {
      await unblockUser({ variables: { userId } });
    } catch {
      return;
    }
  };

  return (
    <Stack spacing={1}>
      <Button
        density="compact"
        disabled={loading}
        onClick={handleUnblock}
        startIcon={unblockIcon}
        tone="neutral"
        variant="outlined"
      >
        {intl.formatMessage({
          id: 'contacts.blocked.unblock',
          defaultMessage: 'Unblock',
        })}
      </Button>
      {error && (
        <Typography color="error" role="alert" variant="caption">
          {error.message}
        </Typography>
      )}
    </Stack>
  );
}

const createBlockedColumns = (
  intl: IntlShape
): MRT_ColumnDef<BlockedUserRow>[] => [
  {
    accessorFn: (row) => row.displayName?.trim() || row.userId,
    header: intl.formatMessage({
      id: 'common.name',
      defaultMessage: 'Name',
    }),
  },
  {
    accessorFn: (row) => new Date(row.blockedAt),
    header: intl.formatMessage({
      id: 'contacts.blocked.at',
      defaultMessage: 'Blocked',
    }),
    Cell: ({ cell }) => formatDate(cell.getValue<Date>()),
  },
  {
    id: 'actions',
    header: intl.formatMessage({
      id: 'general.actions',
      defaultMessage: 'Actions',
    }),
    Cell: ({ row }) => <UnblockAction userId={row.original.userId} />,
    enableSorting: false,
  },
];

const BlockedUsersTable = ({
  loading = false,
  rows,
}: BlockedUsersTableProps) => {
  const intl = useIntl();
  const localization = useMaterialReactTableLocalization();
  const columns = useMemo(() => createBlockedColumns(intl), [intl]);

  const table = useMaterialReactTable({
    ...createBaseTableOptions(loading),
    columns,
    data: rows,
    localization,
  });

  return <MaterialReactTable table={table} />;
};

export {
  BlockedUsersTable,
  ContactsTable,
  IncomingRequestsTable,
  SentRequestsTable,
};
